import { icon } from "../../../shared/icons.js";
import investorsData from "../../../lib/superinvestors.json";

type ComponentState = "idle" | "loading" | "error" | "empty";

interface Investor {
  label: string;
  name: string;
  cik: string;
  people?: string[];
  homepage?: string | null;
  category?: string;
  region?: string;
}

// Precondition: None
// Postcondition: Custom element displaying superinvestors with sidebar filters and search
// Invariant: Investors sorted alphabetically by label, filtered list always subset of full list
class Superinvestors extends HTMLElement {
  private investors: Investor[] = [];
  private filteredInvestors: Investor[] = [];
  private categoryFilter: string = "all";
  private regionFilter: string = "all";
  private searchQuery: string = "";
  private errorMessage: string = "Failed to load investors";

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["state", "fullscreen"];
  }

  get state(): ComponentState {
    return (this.getAttribute("state") as ComponentState) || "idle";
  }

  set state(value: ComponentState) {
    this.setAttribute("state", value);
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue !== newValue && this.shadowRoot?.children.length) {
      if (name === "state") {
        this.updateStateDisplay();
      }
    }
  }

  connectedCallback(): void {
    this.loadData();
    this.render();
    this.setupEventListeners();
  }

  // Precondition: investorsData is valid JSON array
  // Postcondition: this.investors populated with sorted investor data
  private loadData(): void {
    this.investors = (investorsData as Investor[]).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    this.filteredInvestors = [...this.investors];
    this.state = this.investors.length > 0 ? "idle" : "empty";
  }

  private updateStateDisplay(): void {
    const list = this.shadowRoot?.querySelector(".list");
    const count = this.shadowRoot?.querySelector(".count");
    if (!list) return;

    switch (this.state) {
      case "loading":
        list.innerHTML = `<div class="state-container"><ft-spinner text="Loading investors..."></ft-spinner></div>`;
        if (count) count.textContent = "";
        break;
      case "error":
        list.innerHTML = `<div class="state-container error">${this.errorMessage}</div>`;
        if (count) count.textContent = "";
        break;
      case "empty":
        list.innerHTML = `<div class="state-container">No investors available</div>`;
        if (count) count.textContent = "0 investors";
        break;
      case "idle":
        // Handled by updateList
        break;
    }
  }

  // Precondition: this.investors is populated
  // Postcondition: this.filteredInvestors contains investors matching current filters and search
  private applyFilters(): void {
    let result = [...this.investors];

    // Apply category filter
    if (this.categoryFilter !== "all") {
      result = result.filter((inv) => inv.category === this.categoryFilter);
    }

    // Apply region filter
    if (this.regionFilter !== "all") {
      result = result.filter((inv) => inv.region === this.regionFilter);
    }

    // Apply search filter
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.label.toLowerCase().includes(query) ||
          inv.people?.some((p) => p.toLowerCase().includes(query)),
      );
    }

    this.filteredInvestors = result;
  }

  private render(): void {
    if (!this.shadowRoot) return;
    this.shadowRoot.innerHTML = `<style>${this.getStyles()}</style>${this.getTemplate()}`;
  }

  // Precondition: None
  // Postcondition: Updates list without full re-render
  private updateList(): void {
    this.applyFilters();
    const list = this.shadowRoot?.querySelector(".list");
    const count = this.shadowRoot?.querySelector(".count");
    if (!list || !count) return;

    count.textContent = `${this.filteredInvestors.length} investors`;
    list.innerHTML = this.getListItems();
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        font-family: "Inter", system-ui, sans-serif;
        font-size: 14px;
        color: var(--text);
      }

      .wrapper {
        display: grid;
        grid-template-columns: 200px 1fr;
        margin: 0 auto;
        min-height: 480px;
        background: var(--panel);
        border: 1px solid var(--card-border);
        border-radius: 10px;
        overflow: hidden;
      }

      :host([fullscreen]) .wrapper {
        height: 100vh;
        height: 100dvh;
        min-height: unset;
      }

      /* Sidebar */
      .sidebar {
        padding: 20px;
        border-right: 1px solid var(--card-border);
        overflow-y: auto;
      }

      .section {
        margin-bottom: 28px;
      }

      .section:last-child {
        margin-bottom: 0;
      }

      .section-title {
        font-size: 11px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--muted);
        margin-bottom: 10px;
      }

      .filter {
        display: block;
        padding: 7px 0;
        font-size: 13px;
        font-family: inherit;
        color: var(--muted);
        cursor: pointer;
        transition: color 100ms;
        background: none;
        border: none;
        text-align: left;
        width: 100%;
      }

      .filter:hover {
        color: var(--text);
      }

      .filter.active {
        color: var(--primary);
      }

      /* Main */
      .main {
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .header {
        padding: 16px 20px;
        border-bottom: 1px solid var(--card-border);
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .search {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--muted);
      }

      .search svg {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
      }

      .search input {
        flex: 1;
        background: none;
        border: none;
        outline: none;
        font-size: 14px;
        font-family: inherit;
        color: var(--text);
      }

      .search input::placeholder {
        color: var(--input-placeholder);
      }

      .count {
        font-size: 12px;
        color: var(--muted);
      }

      .list {
        flex: 1;
        overflow-y: auto;
      }

      .item {
        display: flex;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 1px solid var(--card-border);
        transition: background 100ms;
        position: relative;
      }

      .item:hover {
        background: var(--tab-bg);
      }

      .item-name {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
      }

      .item-name a {
        color: inherit;
        text-decoration: none;
      }

      .item-name a::after {
        content: "";
        position: absolute;
        inset: 0;
      }

      .item-name a:hover {
        color: var(--primary);
      }

      .item-manager {
        font-size: 13px;
        color: var(--muted);
      }

      .item-link {
        display: inline-flex;
        align-items: center;
        margin-left: 6px;
        color: var(--muted);
        text-decoration: none;
        position: relative;
        z-index: 1;
      }

      .item-link:hover {
        color: var(--primary);
      }

      .item-link svg {
        width: 12px;
        height: 12px;
      }

      .empty {
        padding: 40px 20px;
        text-align: center;
        color: var(--muted);
      }

      .state-container {
        padding: 40px 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--muted);
      }

      .state-container.error {
        color: var(--danger, #dc2626);
      }

      @media (max-width: 640px) {
        .wrapper {
          grid-template-columns: 1fr;
        }
        .sidebar {
          border-right: none;
          border-bottom: 1px solid var(--card-border);
          display: flex;
          gap: 24px;
          padding: 16px;
        }
        .section {
          margin-bottom: 0;
        }
        .item {
          flex-wrap: wrap;
          gap: 8px;
        }
        .item-manager {
          margin-right: 0;
          width: 100%;
          order: 3;
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <div class="wrapper">
        <aside class="sidebar">
          <div class="section" data-filter-type="category">
            <div class="section-title">Category</div>
            <button class="filter active" data-value="all">All investors</button>
            <button class="filter" data-value="value">Value</button>
            <button class="filter" data-value="growth">Growth</button>
            <button class="filter" data-value="quality">Quality</button>
            <button class="filter" data-value="activist">Activist</button>
            <button class="filter" data-value="quant">Quant</button>
          </div>
          <div class="section" data-filter-type="region">
            <div class="section-title">Region</div>
            <button class="filter active" data-value="all">All regions</button>
            <button class="filter" data-value="us">United States</button>
            <button class="filter" data-value="europe">Europe</button>
            <button class="filter" data-value="canada">Canada</button>
          </div>
        </aside>
        <main class="main">
          <header class="header">
            <div class="search">
              ${icon("search", 16)}
              <input type="text" placeholder="Search..." />
            </div>
            <span class="count">${this.filteredInvestors.length} investors</span>
          </header>
          <div class="list">
            ${this.getListItems()}
          </div>
        </main>
      </div>
    `;
  }

  private getListItems(): string {
    if (this.filteredInvestors.length === 0) {
      return `<div class="empty">No investors found</div>`;
    }

    return this.filteredInvestors
      .map(
        (inv) => `
      <div class="item">
        <span class="item-name">
          <a href="/companies/${inv.cik}">${inv.label}</a>${inv.homepage ? `<a href="${inv.homepage}" class="item-link" target="_blank" rel="noopener">${icon("external-link", 12)}</a>` : ""}
        </span>
        ${inv.people?.length ? `<span class="item-manager">${inv.people.join(", ")}</span>` : ""}
      </div>
    `,
      )
      .join("");
  }

  private getHostname(url: string): string {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  }

  private setupEventListeners(): void {
    // Search input
    const searchInput = this.shadowRoot?.querySelector(
      ".search input",
    ) as HTMLInputElement;
    searchInput?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.updateList();
    });

    // Filter buttons
    this.shadowRoot?.querySelectorAll(".filter").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const section = target.closest(".section") as HTMLElement;
        section
          ?.querySelectorAll(".filter")
          .forEach((f) => f.classList.remove("active"));
        target.classList.add("active");

        const filterType = section?.dataset.filterType;
        const value = target.dataset.value || "all";

        if (filterType === "category") {
          this.categoryFilter = value;
        } else if (filterType === "region") {
          this.regionFilter = value;
        }

        this.updateList();
      });
    });
  }

  // Public API

  // Precondition: query is a string
  // Postcondition: List filtered by search query
  public search(query: string): void {
    this.searchQuery = query;
    const searchInput = this.shadowRoot?.querySelector(
      ".search input",
    ) as HTMLInputElement;
    if (searchInput) searchInput.value = query;
    this.updateList();
  }

  // Precondition: None
  // Postcondition: Returns current filtered investor count
  public getCount(): number {
    return this.filteredInvestors.length;
  }

  // Precondition: None
  // Postcondition: Shows loading state
  public setLoading(message?: string): void {
    if (message) this.errorMessage = message;
    this.state = "loading";
  }

  // Precondition: None
  // Postcondition: Shows error state
  public setError(message?: string): void {
    this.errorMessage = message || "Failed to load investors";
    this.state = "error";
  }

  // Precondition: None
  // Postcondition: Shows empty state
  public setEmpty(message?: string): void {
    if (message) this.errorMessage = message;
    this.state = "empty";
  }

  // Precondition: None
  // Postcondition: Clears error and returns to idle or empty based on data
  public clearError(): void {
    this.state = this.investors.length > 0 ? "idle" : "empty";
  }

  // Precondition: data is array of Investor objects
  // Postcondition: Sets data and updates display
  public setData(data: Investor[]): void {
    this.investors = data.sort((a, b) => a.label.localeCompare(b.label));
    this.filteredInvestors = [...this.investors];
    this.state = this.investors.length > 0 ? "idle" : "empty";
    this.updateList();
  }
}

if (!customElements.get("ft-superinvestors")) {
  customElements.define("ft-superinvestors", Superinvestors);
}
